import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { UserService } from "../../../../../core/user/user.service";
import { FormService } from "../../../../../dynamic-forms/form.service";
import { Switch } from "../../../../../dynamic-forms/inputs/switch/switch.model";
import { NumberHelper } from "../../../../../utilities/contracts/number-helper";
import { CodingReviewMode } from "../coding-review-mode.enum";

@Component({
  selector: "app-risk-eve-settings",
  templateUrl: "./risk-eve-settings.component.html",
  styleUrls: ["./risk-eve-settings.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RiskEveSettingsComponent implements OnInit, OnChanges {
  @Input() projectConfigForm: FormGroup;
  @Input() projectAttributes: any;
  @Input() projectName: string;
  @Input() isRiskProject = false;
  @Input() isIvaProject = false;
  @Input() chaseCount: number;

  formGroup: FormGroup;
  firstPassCodingBotEnabled: Switch;
  autoPendEmptyNlpResult: Switch;
  noNetNewOpportunityAutoPend: Switch;
  adminSuppressEve: Switch;
  isRiskBotPopupVisible = false;
  selectedRiskBotOption: string;
  riskBotPopUpText: string;
  readonly riskBotHelperText = "Contact your Customer Success Manager to enable EVE for First Pass Coding.";
  private riskFirstPassBotEnabled = false;

  constructor(
    private readonly formService: FormService,
    private readonly userService: UserService,
    private readonly changeDetector: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.riskFirstPassBotEnabled = this.userService.getUserToken().enableRiskFirstPassBot;
    this.createForm();
    this.projectConfigForm.addControl("riskEveSettingsFormGroup", this.formGroup);
    this.applyFirstPassCodingBotRestrictions();

    if (this.projectAttributes) {
      this.loadRiskEveSettings();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.formGroup) {
      return;
    }

    if (changes.chaseCount || changes.isRiskProject || changes.isIvaProject) {
      this.applyFirstPassCodingBotRestrictions();
    }
    if (changes.projectAttributes && this.projectAttributes) {
      this.loadRiskEveSettings();
    }
  }

  get showHelperText(): boolean {
    return !this.riskFirstPassBotEnabled;
  }

  get isFirstPassCodingBotEnabled(): boolean {
    return this.getNumberValue("firstPassCodingBotEnabled") === 1;
  }

  get showAutoPendEmptyNlpResult(): boolean {
    return this.isRiskProject && this.codingReviewMode !== CodingReviewMode.ENCOUNTER;
  }

  get isCodingReviewModeDxV3(): boolean {
    return this.codingReviewMode === CodingReviewMode.DIAGNOSIS_V3;
  }

  saveTo(projectAttributes: any): void {
    projectAttributes.EnableMrrBot.attributeValue = this.getNumberValue("firstPassCodingBotEnabled");
    projectAttributes.AutoPendEmptyNlpResultChase.attributeValue = this.getNumberValue("autoPendEmptyNlpResult");

    if (this.isRiskProject && this.isCodingReviewModeDxV3) {
      projectAttributes.NoNetNewOpportunityAutoPend.attributeValue = Number(this.formGroup.get("noNetNewOpportunityAutoPend").value ?? 0);
      projectAttributes.AdminSuppressEve.attributeValue = Number(this.formGroup.get("adminSuppressEve").value ?? 0);
    }
  }

  onChangeRiskBot(event: any): void {
    this.isRiskBotPopupVisible = true;
    this.selectedRiskBotOption = event.checked ? "enabling" : "deactivating";
    this.riskBotPopUpText = event.checked ? "All legible/readable medical record charts will automatically be coded by EVE." :
      "New medical record charts will not be coded by EVE. Charts that are currently with EVE will continue through the coding process.";
  }

  onRiskBotPopupButtonClick(action: string): void {
    this.isRiskBotPopupVisible = false;
    if (action === "cancel") {
      this.formGroup.patchValue({
        firstPassCodingBotEnabled: Number(!this.formGroup.get("firstPassCodingBotEnabled").value),
      });
    }
    this.changeDetector.markForCheck();
  }

  private get codingReviewMode(): string {
    return this.projectAttributes?.CodingReviewMode?.attributeValue;
  }

  private createForm(): void {
    this.firstPassCodingBotEnabled = new Switch({
      key: "firstPassCodingBotEnabled",
      label: "EVE for First Pass Coding",
      preceedingOffLabel: "Disable",
      followingOnLabel: "Enable",
      tooltip: "EVE is your virtual team member. EVE (Evidence Validation Engine) is an AI-infused tool that validates your clinical data and uncovers conditions, matching them to ICDs/HCCs.",
    });

    this.autoPendEmptyNlpResult = new Switch({
      key: "autoPendEmptyNlpResult",
      label: "EVE 0 Hits Auto PEND",
      preceedingOffLabel: "Inactive",
      followingOnLabel: "Active",
      tooltip: "All chases processed through EVE with 0 Hits will get automatically pended, skip Data entry, and move to Delivery Status with PC-1020. Default setting is disabled/NO. To view and code all chases regardless of EVE hits select Enable/YES.",
    });

    this.noNetNewOpportunityAutoPend = new Switch({
      key: "noNetNewOpportunityAutoPend",
      label: "No Net New Opportunity Auto Pend",
      preceedingOffLabel: "Disable",
      followingOnLabel: "Enable",
      tooltip: "EVE did not retrieve net new HCC, chase pends with PC-1030 and moves delivery",
    });

    this.adminSuppressEve = new Switch({
      key: "adminSuppressEve",
      label: "EVE Suppression",
      preceedingOffLabel: "Disable",
      followingOnLabel: "Enable",
      tooltip: "When enabled, EVE diagnosis suggestions will be hidden if matched HCC/RxHCC already exists on the member’s Admin claim.Disable this option to always display EVE suggestions, even if matched  HCC/RxHCC  is already exists on the member’s Admin claim.",
    });

    this.formGroup = this.formService.createFormGroup([
      this.firstPassCodingBotEnabled,
      this.autoPendEmptyNlpResult,
      this.noNetNewOpportunityAutoPend,
      this.adminSuppressEve,
    ]);
  }

  private loadRiskEveSettings(): void {
    this.formGroup.patchValue({
      firstPassCodingBotEnabled: Number(this.projectAttributes.EnableMrrBot.attributeValue),
      autoPendEmptyNlpResult: Number(this.projectAttributes.AutoPendEmptyNlpResultChase?.attributeValue),
    });

    if (this.isCodingReviewModeDxV3) {
      this.formGroup.patchValue({
        noNetNewOpportunityAutoPend: Number(this.projectAttributes.NoNetNewOpportunityAutoPend?.attributeValue ?? 0),
        adminSuppressEve: Number(this.projectAttributes.AdminSuppressEve?.attributeValue ?? 0),
      });
    }
    this.changeDetector.markForCheck();
  }

  // EVE for First Pass Coding is locked once the project has chases, or when the service org has not enabled it.
  private applyFirstPassCodingBotRestrictions(): void {
    const control = this.formGroup.get("firstPassCodingBotEnabled");

    if (NumberHelper.isGreaterThan(this.chaseCount, 0)) {
      control.disable();
    }

    if ((this.isRiskProject || this.isIvaProject) && !this.riskFirstPassBotEnabled) {
      control.disable();
      this.firstPassCodingBotEnabled = new Switch({
        ...this.firstPassCodingBotEnabled,
        disabled: true,
        tooltip: "Your Service Organization has not enabled this feature. Please contact your administrator.",
      });
    }
    this.changeDetector.markForCheck();
  }

  private getNumberValue(key: string): number {
    return Number(this.formGroup.get(key).value);
  }
}
